import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import { formatDate } from "../../utils/dateUtils";
import { encrypt } from "../../utils/encryption";

function SalesPaymentMasterPage() {
  const navigate = useNavigate();
  const basePath = "/sales-payment-master";

  // Main CRUD Hook for Sales Master Payment Summaries
  const endpoints = useMemo(
    () => ({
      list: "/sales-payment-master/summary",
      create: "/sales-payment-master",
      edit: (id) => `/sales-payment-master/${id}`,
      update: (id) => `/sales-payment-master/${id}`,
      delete: (id) => `/sales-payment-master/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
  } = useCrud({
    endpoints,
    idKey: "sellingMasterId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  // Navigate to Manage Payments Page
  const handleManagePayments = (item) => {
    navigate(`${basePath}/${encrypt(item.sellingMasterId)}/payments`, {
      state: { sellingMaster: item },
    });
  };

  // Table Columns Definition
  const tableColumns = useMemo(
    () => [
      {
        key: "invoiceNo",
        label: "Invoice No",
        sortable: true,
        className: "font-bold text-slate-800 dark:text-white",
        render: (item) => String(item?.invoiceNo || "N/A"),
      },
      {
        key: "date",
        label: "Invoice Date",
        sortable: true,
        render: (item) => formatDate(item?.date),
      },
      {
        key: "customerName",
        label: "Customer",
        sortable: true,
        render: (item) => item?.customerName || "N/A",
      },
      {
        key: "companyName",
        label: "Company",
        sortable: true,
        render: (item) => item?.companyName || "N/A",
      },
      {
        key: "totalAmount",
        label: "Total Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span className="font-bold text-slate-700 dark:text-slate-200">
            ₹{Number(item?.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        key: "receivedAmount",
        label: "Received Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span className="font-bold text-emerald-600 dark:text-emerald-400">
            ₹{Number(item?.receivedAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        key: "remainingReceivableAmount",
        label: "Remaining",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span
            className={`font-bold ${
              Number(item?.remainingReceivableAmount || 0) <= 0
                ? "text-slate-400"
                : "text-amber-600"
            }`}
          >
            ₹{Number(item?.remainingReceivableAmount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
            })}
          </span>
        ),
      },
      {
        key: "status",
        label: "Status",
        headerClassName: "text-center",
        className: "text-center",
        render: (item) => {
          const remaining = Number(item?.remainingReceivableAmount || 0);
          const received = Number(item?.receivedAmount || 0);

          let badgeClass = "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400";
          let statusText = "Pending";

          if (remaining <= 0) {
            badgeClass = "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
            statusText = "Received";
          } else if (received > 0) {
            badgeClass = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
            statusText = "Partially Received";
          }

          return (
            <span
              className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${badgeClass}`}
            >
              {statusText}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      {/* <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <h1 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
          Sales <span className="text-primary-600">Payments</span>
        </h1>
      </div> */}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        renderActions={(item) => {
          const isFullyReceived = Number(item?.remainingReceivableAmount || 0) <= 0;
          return (
            <Button
              variant="secondary"
              size="sm"
              className={`flex items-center gap-1 h-8 px-2.5 text-xs font-bold uppercase tracking-wider transition-all active:scale-95 ${
                isFullyReceived
                  ? "border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400"
                  : "border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300"
              }`}
              onClick={() => handleManagePayments(item)}
              title={isFullyReceived ? "View Payments" : "Manage Payments"}
            >
              <i className="material-icons text-sm">{isFullyReceived ? "visibility" : "payments"}</i>
              {isFullyReceived ? "View" : "Receive"}
            </Button>
          );
        }}
        showActions={true}
        searchKey="search"
        searchPlaceholder="Search by Invoice No, Customer or Company..."
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default SalesPaymentMasterPage;
